import { Check } from "lucide-react";
import { Reveal } from "./Reveal";

const RITUAL_IMG =
  "https://images.unsplash.com/photo-1741522509438-a120c0bb5e88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxBeXVydmVkaWMlMjBtYXNzYWdlJTIwYWJoeWFuZ2ElMjB0aGVyYXB5fGVufDB8fHx8MTc5MTIzODkxMHww&ixlib=rb-4.1.0&q=85";
const RETREAT_IMG =
  "https://images.unsplash.com/photo-1677559401235-fe6d5ba9a4df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxLZXJhbGElMjBBeXVydmVkYSUyMHJldHJlYXQlMjB0cmVhdG1lbnQlMjByb29tc3xlbnwwfHx8fDE3OTEyMzg5MTB8MA&ixlib=rb-4.1.0&q=85";

const PHASES = [
  {
    num: "01",
    stage: "Before",
    title: "Prepare the Body",
    intro: [
      "We start by understanding you: your constitution, your imbalances, your life — with proper counselling, history and assessment. Through personalised consultation, tailored diet and lifestyle guidance, and preparation protocols, your body is made ready.",
      "Every transformation begins with readiness. How your body is prepared determines how deeply you heal — so the process ahead works with your body, not against it.",
    ],
    points: [
      "A personalised consultation to assess your unique needs",
      "Tailored diet and lifestyle guidance to prepare mind and body",
      "Preparation protocols designed for optimal results",
      "Ongoing support from day one of your journey",
    ],
  },
  {
    num: "02",
    stage: "During",
    title: "Deep Cleansing, Complete Care",
    intro: [
      "Authentic therapies. Trusted practitioners. Consistent monitoring that adapts as your body responds, and nourishing meals aligned to your dosha. This is Ayurveda's most profound reset — held with expert care at every step.",
      "This is where ancient science does its work — carefully, gently, and under expert eyes at every stage.",
    ],
    points: [
      "Authentic Panchakarma therapies, true to tradition",
      "Access to recommended centres and trusted, experienced practitioners",
      "Daily monitoring with treatments customised as your body responds",
      "Nourishing, dosha-aligned meals that support the cleanse from within",
    ],
  },
  {
    num: "03",
    stage: "After",
    title: "Maintain the Transformation",
    intro: [
      "The cleanse ends. The change does not have to. A tailored post-cleanse plan, healthcare tips, and follow-up consultations with accurate monitoring keep you supported long after the programme — so results last, not fade.",
      "The real success of Panchakarma is not just the cleanse — it is what stays with you afterward. This is where most wellness journeys are lost, and where we stay closest to you.",
    ],
    points: [
      "A post-cleanse plan built specifically for your recovery",
      "Guidance on home remedies and daily routines (Dinacharya)",
      "Follow-up consultations to track your progress",
      "Long-term support for wellness that lasts, not fades",
    ],
  },
];

const PhaseCard = ({ phase, className = "", wide = false }) => (
  <div data-testid={`phase-card-${phase.num}`} className={`bg-background p-8 lg:p-14 ${className}`}>
    <div className="flex items-baseline justify-between gap-6">
      <span className="font-serif text-6xl font-light text-accent/30 lg:text-7xl">{phase.num}</span>
      <span className="text-[11px] font-medium uppercase tracking-[0.35em] text-accent">
        {phase.stage}
      </span>
    </div>
    <h3 className="mt-8 font-serif text-2xl font-normal leading-snug sm:text-3xl lg:text-4xl">
      {phase.title}
    </h3>
    {phase.intro.map((p) => (
      <p key={p.slice(0, 24)} className="mt-5 text-sm font-light leading-relaxed text-muted-foreground lg:text-base">
        {p}
      </p>
    ))}
    <ul className={`mt-10 grid gap-4 ${wide ? "sm:grid-cols-2" : ""}`}>
      {phase.points.map((pt) => (
        <li key={pt} className="flex items-start gap-3 text-sm font-light leading-relaxed text-foreground/85">
          <Check size={15} className="mt-1 shrink-0 text-accent" />
          <span>{pt}</span>
        </li>
      ))}
    </ul>
  </div>
);

const ImageTile = ({ src, alt, className = "" }) => (
  <div className={`group relative min-h-[320px] overflow-hidden ${className}`}>
    <img
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-black/20" />
  </div>
);

export const Journey = () => (
  <section id="journey" data-testid="journey-section" className="border-t border-border/50 py-28 lg:py-40">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal>
        <p
          data-testid="journey-overline"
          className="text-[11px] font-medium uppercase tracking-[0.4em] text-accent"
        >
          Life Before and After Panchakarma
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2
          data-testid="journey-heading"
          className="mt-8 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
        >
          A journey in <em className="text-accent">three movements</em>.
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-muted-foreground lg:text-lg">
          Experience Smart Easy Panchakarma the way it was meant to be lived — with expert guidance,
          ancient wisdom with modern art and treatment, and a plan built entirely around you.
        </p>
      </Reveal>

      <Reveal delay={0.1} className="mt-20">
        <div className="grid grid-cols-1 gap-px border border-border/40 bg-border/40 lg:grid-cols-12">
          <PhaseCard phase={PHASES[0]} className="lg:col-span-7" />
          <ImageTile src={RITUAL_IMG} alt="Abhyanga, authentic Ayurvedic massage therapy" className="lg:col-span-5" />
          <ImageTile src={RETREAT_IMG} alt="Kerala Ayurveda retreat treatment surroundings" className="lg:col-span-5" />
          <PhaseCard phase={PHASES[1]} className="lg:col-span-7" />
          <PhaseCard phase={PHASES[2]} className="lg:col-span-12" wide />
        </div>
      </Reveal>
    </div>
  </section>
);
